import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Notification,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import {
  getProduct,
  getProductConfigsPaginatedAndFiltered,
} from "@utils/api-communication/RestApiCli";
import { IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { IProduct, ProductConfig } from "types/productManagementTypes";
import ConfigOverviewPanel from "./ConfigOverviewPanel";
import { ProductFetchStatus } from "./DeviceStatesPanel";
import useProductConfigActions from "./useProductConfigActions";

type Filter = {
  pid?: string;
  defaultConfigName?: string;
  hardwareModel?: string;
};

const ProductConfigList: React.FC = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<ProductConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>({});
  const [pendingFilter, setPendingFilter] = useState<Filter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const [selectedConfig, setSelectedConfig] = useState<ProductConfig | null>(null);
  const [deviceProduct, setDeviceProduct] = useState<IProduct | null>(null);
  const [deviceProductStatus, setDeviceProductStatus] = useState<ProductFetchStatus>("loading");

  const { notification, setNotification } = useProductConfigActions();

  const fetchConfigs = (page = currentPage, filterArg: Filter = filter) => {
    setLoading(true);
    const filterEntries = Object.entries(filterArg).filter(([, v]) => v && String(v).trim() !== "");
    // A default config has no pid, and a regex never matches a missing field, so
    // an empty pid filter is what keeps defaults out — server-side, so the total
    // stays honest.
    const filterObj = { ...Object.fromEntries(filterEntries), pid: filterArg.pid ?? "" };

    getProductConfigsPaginatedAndFiltered(
      {
        limit,
        page,
        filter: JSON.stringify(filterObj),
      },
      (response) => {
        const data = response?.data ?? {};
        const items: ProductConfig[] = Array.isArray(data.data) ? data.data : [];
        setConfigs(items);
        setTotal(typeof data.total === "number" ? data.total : items.length);
        setLoading(false);
      },
      () => {
        setNotification({ type: "error", msg: "Failed to fetch product configs" });
        setConfigs([]);
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    fetchConfigs(currentPage, filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const selectedPid = selectedConfig?.pid;
  useEffect(() => {
    if (!selectedPid) return;
    let cancelled = false;
    setDeviceProduct(null);
    setDeviceProductStatus("loading");
    getProduct(
      selectedPid,
      (response) => {
        if (cancelled) return;
        setDeviceProduct(response?.data ?? null);
        setDeviceProductStatus("loaded");
      },
      () => {
        if (!cancelled) setDeviceProductStatus("error");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedPid]);

  const applyFilters = () => {
    setFilter(pendingFilter);
    setCurrentPage(1);
    fetchConfigs(1, pendingFilter);
  };

  const applyOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") applyFilters();
  };

  const clearFilters = () => {
    setFilter({});
    setPendingFilter({});
    setCurrentPage(1);
    fetchConfigs(1, {});
  };

  const openView = (cfg: ProductConfig) => setSelectedConfig(cfg);

  const openProductStatus = (pid: string) =>
    navigate(`/product-status?pid=${encodeURIComponent(pid)}`, {
      state: { from: "/product-management?tab=configs", fromLabel: "Product Management" },
    });

  const notificationEl = notification && (
    <Notification
      color={notification.type === "success" ? "green" : "red"}
      onClose={() => setNotification(null)}
      withCloseButton
      mb="md"
    >
      {notification.msg}
    </Notification>
  );

  const initialLoading = loading && configs.length === 0;

  return (
    <>
      {!selectedConfig && notificationEl}

      <Group mb="md">
        <TextInput
          placeholder="Filter by PID"
          value={pendingFilter.pid ?? ""}
          onChange={(e) => setPendingFilter({ ...pendingFilter, pid: e.currentTarget.value })}
          onKeyDown={applyOnEnter}
        />
        <TextInput
          placeholder="Filter by Hardware Model"
          value={pendingFilter.hardwareModel ?? ""}
          onChange={(e) =>
            setPendingFilter({ ...pendingFilter, hardwareModel: e.currentTarget.value })
          }
          onKeyDown={applyOnEnter}
        />
        <TextInput
          placeholder="Filter by Default Config Name"
          value={pendingFilter.defaultConfigName ?? ""}
          onChange={(e) =>
            setPendingFilter({ ...pendingFilter, defaultConfigName: e.currentTarget.value })
          }
          onKeyDown={applyOnEnter}
        />
        <Button onClick={applyFilters}>Apply</Button>
        <Button variant="outline" onClick={clearFilters}>
          Clear
        </Button>
      </Group>

      {initialLoading ? (
        <Center style={{ height: "200px" }}>
          <Loader />
        </Center>
      ) : (
        <>
          <Paper withBorder radius="sm" p={0}>
            <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>PID</Table.Th>
                  <Table.Th>Hardware</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Applied</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {configs.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="sm">
                        No configurations found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  configs.map((config, idx) => {
                    const label = config.pid || config.defaultConfigName || `row-${idx}`;
                    const openable = config.type !== "reset";
                    return (
                      <Table.Tr
                        key={`${label}-${idx}`}
                        onClick={() => openable && openView(config)}
                        style={{ cursor: openable ? "pointer" : "default" }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {config.pid}
                          </Text>
                        </Table.Td>
                        <Table.Td>{config.hardwareModel || "-"}</Table.Td>
                        <Table.Td>
                          {config.type ? (
                            <Badge variant="light" color="teal" size="sm">
                              {config.type}
                            </Badge>
                          ) : (
                            "-"
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            variant="light"
                            color={config.applied ? "green" : "yellow"}
                            size="sm"
                          >
                            {config.applied ? "Yes" : "No"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {config.date ? new Date(config.date).toLocaleString() : "-"}
                          </Text>
                        </Table.Td>
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          {openable && (
                            <Button size="xs" onClick={() => openView(config)}>
                              Open
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Paper>

          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={Math.max(1, Math.ceil(total / limit))}
            withControls
            withEdges
            mt="md"
          />
        </>
      )}

      <Modal
        opened={!!selectedConfig}
        onClose={() => setSelectedConfig(null)}
        title={selectedConfig?.pid ? `Config for ${selectedConfig.pid}` : "Default config"}
        size="xl"
      >
        {selectedConfig && (
          <Stack gap="md">
            {notificationEl}
            {selectedConfig.pid && (
              <Group justify="flex-end">
                <Button
                  variant="light"
                  rightSection={<IconExternalLink size={16} />}
                  onClick={() => openProductStatus(selectedConfig.pid as string)}
                >
                  Open Product Status
                </Button>
              </Group>
            )}
            <ConfigOverviewPanel
              config={selectedConfig}
              product={deviceProduct}
              productStatus={deviceProductStatus}
            />
          </Stack>
        )}
      </Modal>
    </>
  );
};

export default ProductConfigList;
